import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { TenantConfigService } from './tenant-config.service';
import {
  ListarRdasPacientesCompositionRequest,
  ListarRdasPacientesCompositionResponse,
  ListarRdasEncuentrosClinicosRequest,
  ListarRdasEncuentrosClinicosResponse,
  ConsultarInmunizacionRequest,
  ConsultarInmunizacionResponse,
  ConsultarPacienteExactoRequest,
  ConsultarPacienteExactoResponse,
  ConsultarRdaCompletoRequest,
  ConsultarRdaCompletoResponse,
} from '../../shared/models/fhir.models';

/**
 * FhirApiService
 *
 * Handles all FHIR API interactions with the backend.
 * Uses TenantConfigService for multi-tenant support:
 * - API URL is resolved dynamically based on the current domain
 * - API Key is injected automatically by TenantApiKeyInterceptor
 *
 * The ApiResponseInterceptor automatically unwraps the response envelope
 * { success, data, timestamp } → data.
 */
@Injectable({
  providedIn: 'root',
})
export class FhirApiService {
  private readonly http = inject(HttpClient);
  private readonly tenantConfig = inject(TenantConfigService);

  /**
   * Returns the base URL for the current tenant.
   * Dynamically resolved based on the current domain/subdomain.
   */
  private get baseUrl(): string {
    return this.tenantConfig.getApiUrl();
  }

  // ── Composition ───────────────────────────────────────────────────────────

  /**
   * Lists all Composition (RDA) resources for a patient.
   *
   * Backend: POST /rda/listar-rdas-pacientes-composition
   * IHCE:    POST /Composition/$consultar-rda-paciente
   *
   * Note: X-API-Key header is injected automatically by TenantApiKeyInterceptor
   */
  listarRdasPacientesComposition(
    request: ListarRdasPacientesCompositionRequest,
  ): Observable<ListarRdasPacientesCompositionResponse> {
    return this.http.post<ListarRdasPacientesCompositionResponse>(
      `${this.baseUrl}/rda/listar-rdas-pacientes-composition`,
      request,
    );
  }

  /**
   * Lists clinical encounter Composition resources for a patient.
   *
   * Backend: POST /rda/listar-rdas-encuentros-clinicos
   * IHCE:    POST /Composition/$consultar-rda-encuentros-clinicos
   *
   * Note: X-API-Key header is injected automatically by TenantApiKeyInterceptor
   */
  listarRdasEncuentrosClinicos(
    request: ListarRdasEncuentrosClinicosRequest,
  ): Observable<ListarRdasEncuentrosClinicosResponse> {
    return this.http.post<ListarRdasEncuentrosClinicosResponse>(
      `${this.baseUrl}/rda/listar-rdas-encuentros-clinicos`,
      request,
    );
  }

  // ── Immunization ──────────────────────────────────────────────────────────

  /**
   * Queries immunization records for a patient.
   *
   * Backend: POST /rda/consultar-inmunizacion
   * IHCE:    POST /Immunization/$consultar-inmunizacion
   *
   * Note: X-API-Key header is injected automatically by TenantApiKeyInterceptor
   */
  consultarInmunizacion(
    request: ConsultarInmunizacionRequest,
  ): Observable<ConsultarInmunizacionResponse> {
    return this.http.post<ConsultarInmunizacionResponse>(
      `${this.baseUrl}/rda/consultar-inmunizacion`,
      request,
    );
  }

  // ── Patient ───────────────────────────────────────────────────────────────

  /**
   * Searches for a patient by exact identifier.
   *
   * Backend: POST /rda/consultar-paciente-exacto
   * IHCE:    POST /Patient/$consultar-paciente-exacto
   *
   * Note: X-API-Key header is injected automatically by TenantApiKeyInterceptor
   */
  consultarPacienteExacto(
    request: ConsultarPacienteExactoRequest,
  ): Observable<ConsultarPacienteExactoResponse> {
    return this.http.post<ConsultarPacienteExactoResponse>(
      `${this.baseUrl}/rda/consultar-paciente-exacto`,
      request,
    );
  }

  // ── Full RDA consultation ─────────────────────────────────────────────────

  /**
   * Queries both RDA sources in parallel and resolves all referenced FHIR
   * resources in a single call.
   *
   * Backend: POST /rda/consultar-rda-completo
   * IHCE (parallel):
   *   - POST /Composition/$consultar-rda-paciente
   *   - POST /Composition/$consultar-rda-encuentros-clinicos
   *
   * The ApiResponseInterceptor unwraps the backend envelope automatically,
   * so this Observable emits ConsultarRdaCompletoResponse directly.
   *
   * Note: X-API-Key header is injected automatically by TenantApiKeyInterceptor
   */
  consultarRdaCompleto(
    request: ConsultarRdaCompletoRequest,
  ): Observable<ConsultarRdaCompletoResponse> {
    return this.http.post<ConsultarRdaCompletoResponse>(
      `${this.baseUrl}/rda/consultar-rda-completo`,
      request,
    );
  }

  // ── Pagination ────────────────────────────────────────────────────────────

  /**
   * Navigates to a FHIR pagination link with resource accumulation.
   *
   * Backend: POST /rda/navegar-pagina
   * IHCE:    GET  <absolute pagination URL from Bundle.link>
   *
   * The backend:
   *  1. Validates the URL originates from the IHCE server (SSRF protection).
   *  2. Fetches the Bundle page.
   *  3. Resolves all FHIR references in the new page.
   *  4. Merges them (de-duplicated) with resources from previous pages,
   *     keyed by patientId or sessionId.
   *
   * Response includes: Bundle + referencedResources + pageReferencedCount + accumulatedCount.
   *
   * Note: X-API-Key header is injected automatically by TenantApiKeyInterceptor
   */
  navegarPagina(url: string, patientId?: string, sessionId?: string): Observable<unknown> {
    return this.http.post<unknown>(`${this.baseUrl}/rda/navegar-pagina`, {
      url,
      patientId,
      sessionId,
    });
  }
}
